import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
} from "@mui/material";
import { getStatusList } from "src/utils/getStatus";
import type { LivestreamData, VideoCategory, VideoData } from "./types";

interface EditDialogProps {
	open: boolean;
	type: "livestream" | "video";
	data: LivestreamData | VideoData | null;
	isNew: boolean;
	isLoading?: boolean;
	categories?: VideoCategory[];
	categoriesLoading?: boolean;
	onClose: () => void;
	onSave: () => void;
	onFieldChange: (field: string, value: string) => void;
}

export default function EditDialog({
	open,
	type,
	data,
	isNew,
	isLoading = false,
	categories = [],
	categoriesLoading = false,
	onClose,
	onSave,
	onFieldChange,
}: EditDialogProps) {
	const getDialogTitle = () => {
		if (type === "livestream") {
			return "Edit Livestream";
		}
		return isNew ? "Add Video" : "Edit Video";
	};

	const isFormValid = () => {
		if (!data) return false;

		const hasRequiredFields = data.title && data.video_uri;

		if (type === "video") {
			const videoData = data as VideoData;
			return hasRequiredFields && videoData.category;
		}

		return hasRequiredFields;
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>{getDialogTitle()}</DialogTitle>
			<DialogContent>
				<Stack spacing={3} sx={{ mt: 1 }}>
					<TextField
						label="Image URL"
						fullWidth
						value={data?.image_uri || ""}
						onChange={(e) => onFieldChange("image_uri", e.target.value)}
						placeholder="https://example.com/image.jpg"
						helperText="Enter image URL for preview"
					/>
					<TextField
						label="Title *"
						fullWidth
						required
						value={data?.title || ""}
						onChange={(e) => onFieldChange("title", e.target.value)}
						error={!data?.title}
						helperText={!data?.title ? "Required field" : ""}
					/>
					{type === "livestream" && (
						<TextField
							label="Description"
							fullWidth
							multiline
							rows={3}
							value={(data as LivestreamData)?.description || ""}
							onChange={(e) => onFieldChange("description", e.target.value)}
							placeholder="Additional information about livestream (optional)"
						/>
					)}
					{type === "video" && (
						<FormControl fullWidth required error={type === "video" && !(data as VideoData)?.category}>
							<InputLabel>Category *</InputLabel>
							<Select
								value={(data as VideoData)?.category_id || ""}
								onChange={(e) => onFieldChange("category_id", String(e.target.value))}
								label="Category *"
								disabled={categoriesLoading}
								endAdornment={categoriesLoading ? <CircularProgress size={20} /> : null}
							>
								{categories.map((category) => (
									<MenuItem key={category.video_category_id} value={category.video_category_id}>
										{category.category_name}
									</MenuItem>
								))}
							</Select>
							{type === "video" && !(data as VideoData)?.category_id && <FormHelperText>Required field</FormHelperText>}
						</FormControl>
					)}
					<TextField
						label="Video URL *"
						fullWidth
						required
						value={data?.video_uri || ""}
						onChange={(e) => onFieldChange("video_uri", e.target.value)}
						error={!data?.video_uri}
						helperText={!data?.video_uri ? "Required field" : "URL to video file or embed link"}
						placeholder="https://example.com/video.mp4 or https://youtube.com/embed/..."
					/>
					<TextField
						label="Order *"
						required
						value={data?.order_index || ""}
						onChange={(e) => onFieldChange("order_index", e.target.value)}
						error={!data?.order_index}
						helperText={!data?.order_index ? "Required field" : ""}
					/>
					<FormControl>
						<InputLabel>Status</InputLabel>
						<Select
							value={(data as VideoData)?.status || 0}
							onChange={(e) => onFieldChange("status", String(e.target.value))}
							label="Status"
						>
							{Object.values(getStatusList()).map((item) => (
								<MenuItem key={item.value} value={item.value}>
									{item.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={isLoading}>
					Cancel
				</Button>
				<Button
					onClick={onSave}
					variant="contained"
					disabled={!isFormValid() || isLoading}
					startIcon={isLoading ? <CircularProgress size={20} /> : null}
				>
					{isLoading ? "Saving..." : "Save"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
